import React from 'react';
import {Icon} from '@iconify/react';
import {SvgSpinners6DotsScale} from '../../../../../incase-domain/components/SvgSpinners6DotsScale';
import {useFetchSlidePreviews, useFetchSlides} from './queries';
import {IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES, TOOLTIP_DELAY} from '../../../../../constants';
import {ImportSlideTabEnum} from '../../../../../incase-domain/common/importTab.enum';
import Pagination from '../../Pagination';
import TooltipWrapper, {TooltipModifiers} from '../../TooltipWrapper';

interface ImportModalSlidesProps {
    isOpen: boolean;
    selectedPresentationId: string | undefined;
    selectSlidesToImport: string[] | null;
    setSelectSlidesToImport: (selectedPresentationId: string[] | null) => void;
    activeTab: ImportSlideTabEnum;
    slidesPage: number;
    setSlidesPage: (page: number) => void;
}

export function ImportModalSlides({
    selectedPresentationId,
    isOpen,
    activeTab,
    selectSlidesToImport,
    setSelectSlidesToImport,
    setSlidesPage,
    slidesPage,
}: ImportModalSlidesProps): JSX.Element {
    const {data: slidesData, isLoading: slidesLoading} = useFetchSlides({
        presentationId: selectedPresentationId || '',
        page: slidesPage,
        size: IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES.pageSize,
        findBookmarked: activeTab === ImportSlideTabEnum.BOOKMARKED,
        enabled: !!selectedPresentationId,
    });

    const slideIds = slidesData?.content.map((slide) => slide._id);

    const {data: slidePreviews, isLoading: slidePreviewsLoading} = useFetchSlidePreviews(slideIds);

    const handleSelectSlide = (id: string): void => {
        if (selectSlidesToImport?.includes(id)) {
            setSelectSlidesToImport(selectSlidesToImport.filter((slideId) => slideId !== id));
        } else {
            setSelectSlidesToImport([...(selectSlidesToImport || []), id]);
        }
    };

    return (
        <>
            {slidesLoading && isOpen && !!selectedPresentationId ? (
                <div className="c-importSlidesModal__loader">
                    <SvgSpinners6DotsScale className="c-importSlidesModal__loader_loading" />
                </div>
            ) : (
                <>
                    <div className="c-importSlidesModal__slides">
                        {slidesData?.content &&
                            slidesData.content.map((slide) => {
                                const slidePreview = slidePreviews?.find((preview) => preview.slideId === slide._id);
                                return (
                                    <div
                                        key={slide._id}
                                        className={`${
                                            selectSlidesToImport?.includes(slide._id)
                                                ? 'c-importSlidesModal__slides_selected'
                                                : 'c-importSlidesModal__slides_unselected'
                                        }`}
                                        onClick={() => handleSelectSlide(slide._id)}
                                    >
                                        {slidePreviewsLoading && (
                                            <div className="c-importSlidesModal__slides_loading">
                                                <SvgSpinners6DotsScale className="c-importSlidesModal__slides_loading_lPreview u-mb-2" />
                                            </div>
                                        )}
                                        {!slidePreviewsLoading && slidePreview && (
                                            <img
                                                src={slidePreview.imgSrc}
                                                alt={`${slide.name} preview`}
                                                className="c-importSlidesModal__slides_slidePreview"
                                            />
                                        )}
                                        {!slidePreviewsLoading && !slidePreview && (
                                            <div className="c-importSlidesModal__slides_slidePreview">
                                                <Icon icon="carbon:no-image" height="3rem" />
                                            </div>
                                        )}
                                        <TooltipWrapper
                                            tooltipClassModifiers={
                                                slide.name && slide.name.length > 30
                                                    ? TooltipModifiers.MULTILINE_TOOLTIP
                                                    : TooltipModifiers.HIDE_SHORT_TOOLTIP
                                            }
                                            id={slide._id}
                                            content={slide.name}
                                            delayShow={TOOLTIP_DELAY}
                                        >
                                            <div className="c-importSlidesModal__slides_slideName">{slide.name}</div>
                                        </TooltipWrapper>
                                    </div>
                                );
                            })}
                    </div>
                    {slidesData && slidesData.numberOfElements > slidesData.pageSize && (
                        <div className="pagination-wrapper u-mt-2">
                            <Pagination
                                numberOfPages={Math.ceil(slidesData.numberOfElements / slidesData.pageSize)}
                                page={slidesData.pageNumber}
                                onPageClick={(pageNumber) => setSlidesPage(pageNumber)}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
}
